import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Button, Flex, Spinner, Text } from "@chakra-ui/react";
import SolvePaper from "./SolvePaper";
import { BASE_API_URL } from "./config";
import { a, useAppColorMode } from "./appColors";
import { dottedSurface } from "./solve/PaperCanvas";
import { solidButton } from "./buttonStyles";

/**
 * A paper opened from a share link (/shared/paper/:token).
 *
 * Signed in or not: the token is sent if there is one, so the owner and
 * collaborators are recognised and may work on the board, and anyone else
 * reads the paper as it is.
 */
const SharedPaperPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { isDark } = useAppColorMode();
  const [paper, setPaper] = useState(null);
  const [problem, setProblem] = useState(null);

  useEffect(() => {
    let live = true;
    (async () => {
      const accessToken = localStorage.getItem("accessToken");
      try {
        const res = await fetch(`${BASE_API_URL}/api/v1/paper-shares/view/${encodeURIComponent(token)}`, {
          headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        });
        const data = await res.json().catch(() => ({}));
        if (!live) return;
        if (!res.ok) {
          setProblem(
            typeof data.detail === "string"
              ? data.detail
              : "This paper could not be opened."
          );
          return;
        }
        setPaper(data);
      } catch {
        if (live) setProblem("This paper could not be opened. Check your connection and try again.");
      }
    })();
    return () => {
      live = false;
    };
  }, [token]);

  if (paper) return <SolvePaper shared={paper} />;

  return (
    <Flex
      className="stande-app"
      data-theme={isDark ? "dark" : "light"}
      minH="100vh"
      bg={a.page}
      sx={dottedSurface(isDark)}
      align="center"
      justify="center"
      px="16px"
    >
      {problem ? (
        <Box
          maxW="420px"
          textAlign="center"
          bg={a.panel}
          border="1px solid"
          borderColor={a.panelBorder}
          borderRadius="18px"
          px="28px"
          py="30px"
        >
          <Text fontSize="17px" fontWeight={600} color={a.text}>
            This paper can’t be opened
          </Text>
          <Text fontSize="13.5px" color={a.textSub} mt="8px" lineHeight="1.5">
            {problem.replace(/\.$/, "")}. If someone shared it with you as a collaborator, sign in with the account they added.
          </Text>
          <Button {...solidButton} mt="20px" borderRadius="12px" fontSize="14px" onClick={() => navigate("/login")}>
            Sign in to Stande
          </Button>
        </Box>
      ) : (
        <Flex align="center" gap="10px" color={a.textSub}>
          <Spinner size="sm" />
          <Text fontSize="13.5px">Opening the paper…</Text>
        </Flex>
      )}
    </Flex>
  );
};

export default SharedPaperPage;
